import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { FileText, Download, ShieldCheck, ArrowLeft, Lock, Eye, Share2, X, ExternalLink } from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';

export default function MyDocuments() {
  const navigate = useNavigate();
  const [docs, setDocs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [previewDoc, setPreviewDoc] = useState(null);
  const API_URL = process.env.REACT_APP_BACKEND_URL;
  const token = localStorage.getItem('userToken');

  useEffect(() => {
    if (!token) {
      navigate('/');
      return;
    }
    axios.get(`${API_URL}/api/my-documents`, { headers: { Authorization: `Bearer ${token}` } })
      .then(res => setDocs(Array.isArray(res.data) ? res.data : []))
      .catch(() => setError('દસ્તાવેજો લોડ કરવામાં નિષ્ફળ'))
      .finally(() => setLoading(false));
  }, [API_URL, token, navigate]);

  // Close preview on Escape
  useEffect(() => {
    if (!previewDoc) return;
    const onKey = (e) => { if (e.key === 'Escape') setPreviewDoc(null); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [previewDoc]);

  const detectKind = (url = '', name = '', contentType = '') => {
    const s = `${name} ${url} ${contentType}`.toLowerCase();
    if (contentType && contentType.includes('pdf')) return 'pdf';
    if (contentType && contentType.startsWith('image/')) return 'image';
    if (/(\.pdf)(\?|#|$)/.test(s)) return 'pdf';
    if (/(\.png|\.jpe?g|\.gif|\.webp|\.svg|\.heic|\.bmp)(\?|#|$)/.test(s)) return 'image';
    return 'other';
  };

  const resolveUrl = (u = '') => {
    if (!u) return '';
    if (u.startsWith('http')) return u;
    if (u.startsWith('/')) return `${API_URL}${u}`;
    return `${API_URL}/api/public/files/${u}`;
  };

  return (
    <div className="min-h-screen bg-[#F8FAFC] font-sans flex flex-col">
      <Header />
      <main className="flex-1 w-full max-w-5xl mx-auto px-4 md:px-8 py-8">
        <button onClick={() => navigate(-1)} className="text-[#172554] hover:text-[#F5B800] flex items-center gap-2 mb-4 text-sm font-medium">
          <ArrowLeft className="w-4 h-4" /> પાછળ
        </button>

        <div className="bg-gradient-to-br from-[#172554] to-[#0f1a3a] rounded-xl shadow-md p-6 mb-6 text-white flex items-center gap-4">
          <div className="w-14 h-14 rounded-xl bg-[#F5B800] flex items-center justify-center shrink-0">
            <ShieldCheck className="w-8 h-8 text-[#172554]" />
          </div>
          <div>
            <h1 className="text-2xl md:text-3xl font-extrabold text-gujarati">મારા દસ્તાવેજો</h1>
            <p className="text-sm text-white/80 text-gujarati mt-1">કાગજબુક દ્વારા તમારા માટે અપલોડ કરેલા દસ્તાવેજો અહીં જુઓ અને ડાઉનલોડ કરો.</p>
            <div className="inline-flex items-center gap-1.5 mt-2 bg-[#F5B800]/20 text-[#F5B800] text-[11px] font-bold px-2 py-0.5 rounded ring-1 ring-[#F5B800]/40">
              <Lock className="w-3 h-3" /> View & Download Only
            </div>
          </div>
        </div>

        {loading ? (
          <div className="text-center py-12 text-[#64748B]">Loading…</div>
        ) : error ? (
          <div className="text-center py-12 text-red-500 text-gujarati">{error}</div>
        ) : docs.length === 0 ? (
          <div className="bg-white rounded-xl border border-dashed border-gray-300 p-12 text-center text-[#64748B]" data-testid="documents-empty">
            <FileText className="w-14 h-14 mx-auto text-gray-300 mb-3" />
            <p className="text-gujarati text-sm">
              હજુ કોઈ દસ્તાવેજ ઉપલબ્ધ નથી.<br/>
              જ્યારે Admin તમારી પૂર્ણ થયેલી અરજી માટે દસ્તાવેજ અપલોડ કરશે, તે અહીં દેખાશે.
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="documents-list">
            {docs.map((d, idx) => (
              <div
                key={d.id || idx}
                className="bg-white rounded-xl shadow-sm border border-gray-100 hover:shadow-md transition-shadow p-4 flex items-center gap-4"
                data-testid={`document-card-${idx}`}
              >
                <div className="w-12 h-12 rounded-lg bg-[#F5B800]/15 flex items-center justify-center shrink-0">
                  <FileText className="w-6 h-6 text-[#F5B800]" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="font-bold text-[#172554] text-gujarati truncate">{d.name || 'Document'}</div>
                  <div className="text-xs text-[#64748B] mt-0.5 flex items-center gap-2 flex-wrap">
                    <span className="font-mono bg-gray-100 px-1.5 py-0.5 rounded text-[10px]">{d.application_number}</span>
                    {d.sub_category_name && <span className="text-gujarati truncate">{d.sub_category_name}</span>}
                    {d.uploaded_at && <span>{new Date(d.uploaded_at).toLocaleDateString('en-GB')}</span>}
                  </div>
                </div>
                {d.url && (
                  <div className="flex flex-col sm:flex-row gap-2 shrink-0" data-testid={`document-actions-${idx}`}>
                    <button
                      onClick={() => setPreviewDoc({ ...d, url: resolveUrl(d.url), kind: detectKind(d.url, d.name, d.content_type) })}
                      className="inline-flex items-center gap-1.5 bg-white border-2 border-[#172554] text-[#172554] hover:bg-[#172554] hover:text-white font-bold px-3 py-2 rounded text-xs transition-colors"
                      data-testid={`document-view-${idx}`}
                      title="View document"
                    >
                      <Eye className="w-4 h-4" />
                      <span className="hidden sm:inline">View</span>
                    </button>
                    <a
                      href={resolveUrl(d.url)}
                      download={d.name || 'document'}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-1.5 bg-[#172554] hover:bg-[#0f1a3a] text-white font-bold px-3 py-2 rounded text-xs transition-colors"
                      data-testid={`document-download-${idx}`}
                      title="Download document"
                    >
                      <Download className="w-4 h-4" />
                      <span className="hidden sm:inline">Download</span>
                    </a>
                    <a
                      href={`https://wa.me/?text=${encodeURIComponent(`નમસ્તે,\n${d.name || 'Document'} — Kagajbook દ્વારા મળેલો દસ્તાવેજ (અરજી ${d.application_number || ''}):\n${resolveUrl(d.url)}`)}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-1.5 bg-green-500 hover:bg-green-600 text-white font-bold px-3 py-2 rounded text-xs transition-colors"
                      data-testid={`document-share-${idx}`}
                      title="Share on WhatsApp"
                    >
                      <Share2 className="w-4 h-4" />
                      <span className="hidden sm:inline">Share</span>
                    </a>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </main>

      {/* Inline Preview Modal — supports PDFs (iframe), images (native <img>), and falls back to "open in new tab" */}
      {previewDoc && (
        <div
          className="fixed inset-0 z-[9998] bg-black/80 backdrop-blur-sm flex flex-col p-3 sm:p-6"
          onClick={() => setPreviewDoc(null)}
          data-testid="document-preview-modal"
        >
          <div
            className="bg-white rounded-xl shadow-2xl w-full max-w-5xl mx-auto flex flex-col overflow-hidden max-h-full flex-1"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Preview Header */}
            <div className="bg-[#172554] text-white px-4 sm:px-5 py-3 flex items-center gap-3 shrink-0">
              <div className="w-9 h-9 rounded-lg bg-[#F5B800] flex items-center justify-center shrink-0">
                <FileText className="w-5 h-5 text-[#172554]" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="font-bold truncate text-gujarati">{previewDoc.name || 'Document'}</div>
                <div className="text-[10px] text-white/70 flex items-center gap-2 flex-wrap">
                  {previewDoc.application_number && <span className="font-mono">{previewDoc.application_number}</span>}
                  <span className="uppercase text-[#F5B800] font-bold">{previewDoc.kind || 'file'}</span>
                </div>
              </div>
              <a
                href={previewDoc.url}
                target="_blank"
                rel="noopener noreferrer"
                className="hidden sm:inline-flex items-center gap-1.5 bg-white/10 hover:bg-white/20 text-white text-xs font-bold px-3 py-1.5 rounded transition-colors"
                data-testid="preview-open-new-tab"
                title="Open in new tab"
              >
                <ExternalLink className="w-3.5 h-3.5" /> New Tab
              </a>
              <a
                href={previewDoc.url}
                download={previewDoc.name || 'document'}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1.5 bg-[#F5B800] hover:bg-[#FFC928] text-[#172554] text-xs font-bold px-3 py-1.5 rounded transition-colors"
                data-testid="preview-download-btn"
              >
                <Download className="w-3.5 h-3.5" /> Download
              </a>
              <button
                onClick={() => setPreviewDoc(null)}
                className="p-1.5 rounded-full hover:bg-white/20 transition-colors"
                aria-label="Close preview"
                data-testid="preview-close-btn"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Preview Body */}
            <div className="flex-1 bg-[#0f172a]/5 overflow-auto flex items-center justify-center min-h-[300px] relative">
              {previewDoc.kind === 'pdf' ? (
                <>
                  <iframe
                    src={`${previewDoc.url}#toolbar=1&navpanes=0`}
                    title={previewDoc.name || 'PDF Preview'}
                    className="w-full h-full min-h-[70vh]"
                    data-testid="preview-pdf-iframe"
                  />
                  {/* Prominent fallback in case the browser refuses inline PDF rendering */}
                  <div className="absolute bottom-3 left-1/2 -translate-x-1/2 flex gap-2 z-10">
                    <a
                      href={previewDoc.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-1.5 bg-white/95 hover:bg-white text-[#172554] font-bold px-3 py-2 rounded shadow text-xs"
                      data-testid="preview-pdf-open-new-tab"
                    >
                      <ExternalLink className="w-4 h-4" /> Open in New Tab
                    </a>
                    <a
                      href={previewDoc.url}
                      download={previewDoc.name || 'document'}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-1.5 bg-[#F5B800] hover:bg-[#FFC928] text-[#172554] font-bold px-3 py-2 rounded shadow text-xs"
                      data-testid="preview-pdf-download"
                    >
                      <Download className="w-4 h-4" /> Download
                    </a>
                  </div>
                </>
              ) : previewDoc.kind === 'image' ? (
                <img
                  src={previewDoc.url}
                  alt={previewDoc.name || 'Preview'}
                  className="max-w-full max-h-full object-contain"
                  data-testid="preview-image"
                />
              ) : (
                <div className="p-8 text-center text-[#64748B] flex flex-col items-center gap-3">
                  <FileText className="w-12 h-12 text-gray-300" />
                  <p className="text-sm text-gujarati">આ ફોર્મેટ ઇનલાઇન બતાવી શકાતું નથી.</p>
                  <div className="flex gap-2">
                    <a
                      href={previewDoc.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-1.5 bg-[#172554] hover:bg-[#0f1a3a] text-white font-bold px-4 py-2 rounded text-sm transition-colors"
                    >
                      <ExternalLink className="w-4 h-4" /> Open in new tab
                    </a>
                    <a
                      href={previewDoc.url}
                      download={previewDoc.name || 'document'}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-1.5 bg-[#F5B800] hover:bg-[#FFC928] text-[#172554] font-bold px-4 py-2 rounded text-sm transition-colors"
                    >
                      <Download className="w-4 h-4" /> Download
                    </a>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      <Footer />
    </div>
  );
}
